import React, { useState, useEffect } from 'react';
import apiService, { Currency, MinimalExchangeAmount, ExchangeAmount, Transaction } from '../services/api';
import TransactionHistoryService from '../services/transactionHistory';
import { Container, Row, Col, Form, Button, Card, Alert, Spinner, Badge, ProgressBar, InputGroup, Modal } from 'react-bootstrap';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faWallet, faShieldAlt, faCheckCircle, faClock, faExclamationTriangle, faArrowRight, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import CryptoIcon from './CryptoIcons';
import { QRCodeSVG } from 'qrcode.react';

import '../styles/Exchange.css';

const Exchange: React.FC = () => {
  const transactionHistoryService = TransactionHistoryService.getInstance();
  // State for currencies and exchange
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [fromCurrency, setFromCurrency] = useState<string>('');
  const [toCurrency, setToCurrency] = useState<string>('');
  const [availablePairs, setAvailablePairs] = useState<Currency[]>([]);
  const [amount, setAmount] = useState<string>('');
  const [minAmount, setMinAmount] = useState<number>(0);
  const [estimatedAmount, setEstimatedAmount] = useState<number | null>(null);
  const [walletAddress, setWalletAddress] = useState<string>('');
  const [extraId, setExtraId] = useState<string>('');
  const [refundAddress, setRefundAddress] = useState<string>('');
  const [refundExtraId, setRefundExtraId] = useState<string>('');
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const [transactionStatus, setTransactionStatus] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isFixedRate, setIsFixedRate] = useState<boolean>(false);
  const [step, setStep] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  // Add state for modal
  const [showFixedRateModal, setShowFixedRateModal] = useState<boolean>(false);

  // Fetch available currencies on component mount
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        setIsLoading(true);
        const data = await apiService.getAvailableCurrencies(true, isFixedRate);
        setCurrencies(data);
        setIsLoading(false);
      } catch (err) {
        setError('Failed to fetch currencies. Please try again.');
        setIsLoading(false);
      }
    };

    fetchCurrencies();
  }, [isFixedRate]);

  // Fetch available pairs when fromCurrency changes
  useEffect(() => {
    if (fromCurrency) {
      const fetchAvailablePairs = async () => {
        try {
          setIsLoading(true);
          const data = await apiService.getAvailablePairs(fromCurrency, isFixedRate);
          setAvailablePairs(data);
          setIsLoading(false);
        } catch (err) {
          setError('Failed to fetch available pairs. Please try again.');
          setIsLoading(false);
        }
      };

      fetchAvailablePairs();
    }
  }, [fromCurrency, isFixedRate]);

  // Fetch minimum amount when currency pair is selected
  useEffect(() => {
    if (fromCurrency && toCurrency) {
      const fetchMinAmount = async () => {
        try {
          setIsLoading(true);
          setError(''); // Clear any previous errors
          
          console.log(`Fetching ${isFixedRate ? 'fixed rate range' : 'min amount'} for ${fromCurrency}_${toCurrency}`);
          
          if (isFixedRate) {
            try {
              const data = await apiService.getFixedRateRange(fromCurrency, toCurrency);
              console.log('Fixed rate range response:', data);
              setMinAmount(data.minAmount);
            } catch (fixedRateError: any) {
              console.error('Fixed rate error:', fixedRateError);
              
              // Check for fixed_rate_not_enabled error
              if (fixedRateError.message && fixedRateError.message.includes('fixed_rate_not_enabled')) {
                // Switch to standard exchange and notify the user
                setIsFixedRate(false);
                setError('Fixed rate is not available for this pair. Switched to standard exchange.');
                // Show the modal instead of just setting an error
                setShowFixedRateModal(true);
                
                // Fetch standard min amount instead
                const standardData = await apiService.getMinimalExchangeAmount(fromCurrency, toCurrency);
                setMinAmount(standardData.minAmount);
              } else {
                throw fixedRateError; // Re-throw if it's a different error
              }
            }
          } else {
            const data = await apiService.getMinimalExchangeAmount(fromCurrency, toCurrency);
            console.log('Min amount response:', data);
            setMinAmount(data.minAmount);
          }
          setIsLoading(false);
        } catch (err: any) {
          console.error('Error fetching min amount:', err);
          
          // Improved error handling
          let errorMessage = 'Failed to fetch minimum amount. Please try again.';
          
          if (err.message && err.message.includes('API Error:')) {
            errorMessage = err.message;
          } else if (err.response && err.response.data && err.response.data.error) {
            errorMessage = `API Error: ${err.response.data.error}`;
          }
          
          setError(errorMessage);
          setIsLoading(false);
        }
      };

      fetchMinAmount();
    }
  }, [fromCurrency, toCurrency, isFixedRate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isFixedRate && transaction && timeRemaining === null) {
      // Set initial time to 20 minutes (1200 seconds)
      setTimeRemaining(1200);
      timer = setInterval(() => {
        setTimeRemaining(prev => prev !== null ? Math.max(0, prev - 1) : null);
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isFixedRate, transaction]);

  // Handle from currency change
  const handleFromCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFromCurrency(e.target.value);
    setToCurrency('');
    setAmount('');
    setEstimatedAmount(null);
    setStep(1);
  };

  // Handle to currency change
  const handleToCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setToCurrency(e.target.value);
    setAmount('');
    setEstimatedAmount(null);
    setStep(1);
  };

  // Handle amount change
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(e.target.value);
    setEstimatedAmount(null);
  };

  // Get estimated exchange amount
  const getEstimatedAmount = async () => {
    if (!fromCurrency || !toCurrency || !amount) {
      setError('Please select currencies and enter an amount.');
      return;
    }

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) {
      setError('Please enter a valid amount.');
      return;
    }

    if (numAmount < minAmount) {
      setError(`Minimum amount for this pair is ${minAmount} ${fromCurrency}.`);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      let data: ExchangeAmount;

      // Log request parameters for debugging
      console.log('Requesting estimate with params:', { 
        fromCurrency, 
        toCurrency, 
        amount: numAmount,
        isFixedRate 
      });

      if (isFixedRate) {
        data = await apiService.getEstimatedFixedRateAmount(fromCurrency, toCurrency, numAmount);
      } else {
        data = await apiService.getEstimatedExchangeAmount(fromCurrency, toCurrency, numAmount);
      }

      // Log response for debugging
      console.log('Estimate response:', data);

      if (!data || typeof data.estimatedAmount !== 'number') {
        console.error('Invalid response format:', data);
        setError('Invalid response from exchange service. Please try again later.');
        setIsLoading(false);
        return;
      }

      setEstimatedAmount(data.estimatedAmount);
      setStep(2);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Estimate error:', err);
      
      // Improved error handling for network errors
      let errorMessage = 'Failed to get estimated amount';
      
      if (err.message === 'Network Error') {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (err.response) {
        // API returned an error response
        if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
        } else if (err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `API error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(`Error: ${errorMessage}`);
      setIsLoading(false);
    }
  };

  // Create exchange transaction
  const createTransaction = async () => {
    if (!walletAddress) {
      setError('Please enter a wallet address.');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const numAmount = parseFloat(amount);
      let data: Transaction;

      console.log('Creating transaction with params:', {
        fromCurrency,
        toCurrency,
        amount: numAmount,
        walletAddress,
        extraId,
        refundAddress,
        refundExtraId,
        isFixedRate
      });

      if (isFixedRate) {
        data = await apiService.createFixedRateTransaction(
          fromCurrency,
          toCurrency,
          numAmount,
          walletAddress,
          extraId,
          refundAddress,
          refundExtraId
        );
      } else {
        data = await apiService.createExchangeTransaction(
          fromCurrency,
          toCurrency,
          numAmount,
          walletAddress,
          extraId,
          refundAddress,
          refundExtraId
        );
      }

      // In your createTransaction function
      console.log('Transaction created:', data);
      setTransaction(data);
      setTransactionStatus(data.status || 'pending'); // Provide a default value
      transactionHistoryService.addTransaction(data);
      setStep(3);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Transaction creation error:', err);
      
      // Improved error handling for network errors
      let errorMessage = 'Failed to create transaction';
      
      if (err.message === 'Network Error') {
        errorMessage = 'Network connection error. Please check your internet connection and try again.';
      } else if (err.response) {
        if (err.response.data?.error) {
          errorMessage = err.response.data.error;
        } else if (err.response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
        } else if (err.response.status >= 500) {
          errorMessage = 'Server error. Please try again later.';
        } else {
          errorMessage = `API error (${err.response.status}): ${err.response.statusText}`;
        }
      } else if (err.request) {
        errorMessage = 'No response from server. Please try again later.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(`Error: ${errorMessage}`);
      setIsLoading(false);
    }
  };

  // Check transaction status
  const checkTransactionStatus = async () => {
    if (!transaction) return;

    try {
      setIsLoading(true);
      const data = await apiService.getTransactionStatus(transaction.id);
      setTransactionStatus(data.status);
      setIsLoading(false);
    } catch (err) {
      setError('Failed to check transaction status. Please try again.');
      setIsLoading(false);
    }
  };

  // Reset exchange form
  const resetExchange = () => {
    setFromCurrency('');
    setToCurrency('');
    setAmount('');
    setEstimatedAmount(null);
    setWalletAddress('');
    setExtraId('');
    setRefundAddress('');
    setRefundExtraId('');
    setTransaction(null);
    setTransactionStatus('');
    setError('');
    setStep(1);
  };

  // Helper function to render currency icon
  const renderCurrencyIcon = (ticker: string, size = 32) => {
    return <CryptoIcon symbol={ticker} size={size} className="currency-icon" />;
  };

  // Helper function to format status for display
  // Format transaction status for display
  const formatStatus = (status: string | undefined) => {
    if (!status) return 'Unknown';
    
    return status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  };

  return (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body>
          <h1 className="text-center mb-4">
            <FontAwesomeIcon icon={faExchangeAlt} className="me-2" bounce />
            Swap Your Crypto
          </h1>
          
          <div className="d-flex justify-content-end mb-4">
            <div className="exchange-type-toggle-wrapper">
              <Form.Check 
                type="switch"
                id="exchange-type-toggle"
                label={
                  <span>
                    {isFixedRate ? 
                      <><FontAwesomeIcon icon={faShieldAlt} className="me-2 text-success" /> Fixed Rate (Guaranteed)</> : 
                      <><FontAwesomeIcon icon={faExchangeAlt} className="me-2" /> Standard Exchange</>}
                  </span>
                }
                checked={isFixedRate}
                onChange={() => setIsFixedRate(!isFixedRate)}
                className="custom-switch"
              />
            </div>
          </div>

          {error && 
            <Alert variant="danger">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              {error}
            </Alert>
          }

          {isLoading && (
            <div className="text-center py-3 loading-animation">
              <Spinner animation="border" variant="primary" />
              <span className="ms-2">Processing your request...</span>
            </div>
          )}

          {/* Step indicator */}
          <div className="step-indicator-container mb-4">
            <ProgressBar className="mb-2">
              <ProgressBar variant={step >= 1 ? "primary" : "secondary"} now={33.3} key={1} />
              <ProgressBar variant={step >= 2 ? "primary" : "secondary"} now={33.3} key={2} />
              <ProgressBar variant={step === 3 ? "primary" : "secondary"} now={33.3} key={3} />
            </ProgressBar>
            <div className="d-flex justify-content-between">
              <div className={`step-label ${step >= 1 ? 'active' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-text">Select</span>
              </div>
              <div className={`step-label ${step >= 2 ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-text">Confirm</span>
              </div>
              <div className={`step-label ${step >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-text">Exchange</span>
              </div>
            </div>
          </div>

          <TransitionGroup className="exchange-steps">
            {step === 1 && (
              <CSSTransition key="step1" timeout={400} classNames="fade">
                <div className="exchange-form">
                  <h2 className="mb-4">
                    <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                    Select Currencies and Amount
                  </h2>
                  
                  <Form>
                    <Row className="mb-4">
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>From Currency:</Form.Label>
                          <div className="currency-select-container">
                            <Form.Select 
                              value={fromCurrency}
                              onChange={handleFromCurrencyChange}
                              disabled={isLoading}
                              className="currency-select"
                            >
                              <option value="">Select currency</option>
                              {currencies.map((currency) => (
                                <option key={currency.ticker} value={currency.ticker}>
                                  {currency.name} ({currency.ticker})
                                </option>
                              ))}
                            </Form.Select>
                            {fromCurrency && (
                              <div className="selected-currency-icon">
                                {renderCurrencyIcon(fromCurrency, 24)}
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                      
                      <Col md={2} className="d-flex align-items-center justify-content-center">
                        <div className="exchange-arrow">
                          <FontAwesomeIcon icon={faArrowRight} size="2x" className="text-primary" pulse />
                        </div>
                      </Col>
                      
                      <Col md={5}>
                        <Form.Group className="mb-3">
                          <Form.Label>To Currency:</Form.Label>
                          <div className="currency-select-container">
                            <Form.Select
                              value={toCurrency}
                              onChange={handleToCurrencyChange}
                              disabled={isLoading || !fromCurrency}
                              className="currency-select"
                            >
                              <option value="">Select currency</option>
                              {availablePairs.map((currency) => (
                                <option key={currency.ticker} value={currency.ticker}>
                                  {currency.name} ({currency.ticker})
                                </option>
                              ))}
                            </Form.Select>
                            {toCurrency && (
                              <div className="selected-currency-icon">
                                {renderCurrencyIcon(toCurrency, 24)}
                              </div>
                            )}
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Form.Group className="mb-4">
                      <Form.Label>
                        <FontAwesomeIcon icon={faWallet} className="me-2" />
                        Amount to Exchange:
                      </Form.Label>
                      <InputGroup className="amount-input-group">
                        <Form.Control
                          type="number"
                          value={amount}
                          onChange={handleAmountChange}
                          placeholder={`Min: ${minAmount} ${fromCurrency}`}
                          disabled={isLoading || !fromCurrency || !toCurrency}
                          className="amount-input"
                        />
                        {fromCurrency && (
                          <InputGroup.Text className="currency-tag">
                            {renderCurrencyIcon(fromCurrency, 18)}
                            <span className="ms-1">{fromCurrency}</span>
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                      {minAmount > 0 && (
                        <Form.Text className="text-muted min-amount-info">
                          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                          Minimum amount: {minAmount} {fromCurrency}
                        </Form.Text>
                      )}
                    </Form.Group>
                    
                    <div className="d-grid gap-2">
                      <Button 
                        variant="primary" 
                        onClick={getEstimatedAmount}
                        disabled={isLoading || !fromCurrency || !toCurrency || !amount}
                        className="exchange-button"
                      >
                        <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                        Get Estimated Amount
                      </Button>
                    </div>
                  </Form>
                </div>
              </CSSTransition>
            )}

            {step === 2 && (
              <CSSTransition key="step2" timeout={400} classNames="fade">
                <div className="exchange-form">
                  <h2 className="mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Confirm Exchange Details
                  </h2>
                  
                  <Card className="exchange-summary-card mb-4">
                    <Card.Body>
                      <Row className="mb-3">
                        <Col md={5}>
                          <h5 className="exchange-summary-title">You Send:</h5>
                          <div className="exchange-amount-display">
                            <div className="currency-icon-large">
                              {renderCurrencyIcon(fromCurrency, 48)}
                            </div>
                            <div className="amount-details">
                              <span className="amount-value">{amount}</span>
                              <span className="amount-ticker">{fromCurrency}</span>
                            </div>
                          </div>
                        </Col>
                        
                        <Col md={2} className="d-flex align-items-center justify-content-center">
                          <div className="exchange-arrow-large">
                            <FontAwesomeIcon icon={faArrowRight} size="2x" className="text-primary" pulse />
                          </div>
                        </Col>
                        
                        <Col md={5}>
                          <h5 className="exchange-summary-title">You Receive (Estimated):</h5>
                          <div className="exchange-amount-display">
                            <div className="currency-icon-large">
                              {renderCurrencyIcon(toCurrency, 48)}
                            </div>
                            <div className="amount-details">
                              <span className="amount-value">{estimatedAmount}</span>
                              <span className="amount-ticker">{toCurrency}</span>
                            </div>
                          </div>
                          {isFixedRate && (
                            <Badge bg="success" className="fixed-rate-badge">
                              <FontAwesomeIcon icon={faShieldAlt} className="me-1" /> Fixed Rate
                            </Badge>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
              
              <Form className="wallet-form">
                <Form.Group className="mb-4">
                  <Form.Label className="wallet-form-label">
                    <FontAwesomeIcon icon={faWallet} className="me-2" />
                    Recipient Wallet Address:
                  </Form.Label>
                  <InputGroup className="wallet-input-group">
                    <InputGroup.Text className="wallet-icon">
                      {renderCurrencyIcon(toCurrency, 20)}
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={walletAddress}
                      onChange={(e) => setWalletAddress(e.target.value)}
                      placeholder="Enter recipient wallet address"
                      disabled={isLoading}
                      className="wallet-address-input"
                    />
                  </InputGroup>
                </Form.Group>
                
                {/* Optional Extra ID field */}
                <Form.Group className="mb-4">
                  <Form.Label className="wallet-form-label">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    Extra ID (Optional):
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={extraId}
                    onChange={(e) => setExtraId(e.target.value)}
                    placeholder="Enter extra ID if required"
                    disabled={isLoading}
                    className="extra-id-input"
                  />
                  <Form.Text className="text-muted extra-id-info">
                    <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
                    Required for some currencies (e.g., XRP Destination Tag, XMR Payment ID)
                  </Form.Text>
                </Form.Group>
                
                <Form.Group className="mb-4">
                  <Form.Label className="wallet-form-label">
                    <FontAwesomeIcon icon={faWallet} className="me-2" />
                    Refund Address (Optional):
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={refundAddress}
                    onChange={(e) => setRefundAddress(e.target.value)}
                    placeholder="Enter refund address"
                    disabled={isLoading}
                    className="refund-address-input"
                  />
                </Form.Group>
                
                {/* Optional Refund Extra ID field */}
                <Form.Group className="mb-4">
                  <Form.Label className="wallet-form-label">
                    <FontAwesomeIcon icon={faShieldAlt} className="me-2" />
                    Refund Extra ID (Optional):
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={refundExtraId}
                    onChange={(e) => setRefundExtraId(e.target.value)}
                    placeholder="Enter refund extra ID if required"
                    disabled={isLoading}
                    className="refund-extra-id-input"
                  />
                </Form.Group>
                
                <div className="d-grid gap-3 mt-4">
                  <Button 
                    variant="primary" 
                    onClick={createTransaction}
                    disabled={isLoading || !walletAddress}
                    className="create-transaction-button"
                  >
                    <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
                    Create Transaction
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    onClick={() => setStep(1)}
                    disabled={isLoading}
                    className="back-button"
                  >
                    Back to Selection
                  </Button>
                </div>
              </Form>
            </div>
          </CSSTransition>
          )}
          
          {step === 3 && transaction && (
              <CSSTransition key="step3" timeout={400} classNames="fade">
                <div className="exchange-status">
                  <h2 className="mb-4">
                    <FontAwesomeIcon icon={faClock} className="me-2" />
                    Transaction Status
                  </h2>
                  
                  {/* Add QR Code section here */}

                  <Card className="mb-4">
                    <Card.Body className="text-center">
                      <h5 className="mb-3">Scan to Send Payment</h5>
                      <QRCodeSVG 
                        value={transaction.payinAddress}
                        size={200}
                        level="H"
                        includeMargin={true}
                        className="mb-3"
                      />
                      <p className="mb-2">Send exactly: {parseFloat(amount)} {transaction.fromCurrency}</p>
                      <p className="text-muted small">To address:</p>
                      <code className="d-block mb-2">{transaction.payinAddress}</code>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => navigator.clipboard.writeText(transaction.payinAddress)}
                      >
                        Copy Address
                      </Button>
                    </Card.Body>
                  </Card>

                  {isFixedRate && timeRemaining !== null && (
                    <Alert variant={timeRemaining > 300 ? "info" : "warning"} className="mt-3">
                      <FontAwesomeIcon icon={faClock} className="me-2" />
                      Time remaining to send payment: {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                    </Alert>
                  )}

                  <Card className="transaction-status-card mb-4">
                    <Card.Body>
                      <div className="transaction-id-section mb-4">
                        <h5 className="transaction-section-title">Transaction ID:</h5>
                        <code className="transaction-id">{transaction.id}</code>
                      </div>
                      
                      <div className="transaction-status-section mb-4">
                        <h5 className="transaction-section-title">Status:</h5>
                        <div className="status-badge-container">
                          <Badge 
                            bg={
                              transactionStatus === 'finished' ? 'success' :
                              transactionStatus === 'failed' ? 'danger' :
                              'warning'
                            }
                            className="status-badge"
                          >
                            <FontAwesomeIcon 
                              icon={
                                transactionStatus === 'finished' ? faCheckCircle :
                                transactionStatus === 'failed' ? faExclamationTriangle :
                                faClock
                              } 
                              className="me-2" 
                              pulse={transactionStatus !== 'finished' && transactionStatus !== 'failed'}
                            />
                            {formatStatus(transactionStatus)}
                          </Badge>
                        </div>
                      </div>
                      
                      <Row>
                        <Col md={6}>
                          <div className="transaction-details-section mb-3">
                            <h5 className="transaction-section-title">
                              <FontAwesomeIcon icon={faWallet} className="me-2 text-primary" />
                              Send:
                            </h5>
                            <div className="transaction-detail-item">
                              <div className="d-flex align-items-center mb-2">
                                {renderCurrencyIcon(transaction.fromCurrency, 24)}
                                <span className="ms-2 transaction-amount text-body">
                                  {parseFloat(amount)} {transaction.fromCurrency}
                                </span>
                              </div>
                              <div className="transaction-address">
                                <span className="address-label">Address:</span>
                                <code className="address-value">{transaction.payinAddress}</code>
                              </div>
                              {transaction.payinExtraId && (
                                <div className="transaction-extra-id">
                                  <span className="extra-id-label">Extra ID:</span>
                                  <code className="extra-id-value">{transaction.payinExtraId}</code>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                        
                        <Col md={6}>
                          <div className="transaction-details-section mb-3">
                            <h5 className="transaction-section-title">
                              <FontAwesomeIcon icon={faWallet} className="me-2 text-success" />
                              Receive:
                            </h5>
                            <div className="transaction-detail-item">
                              <div className="d-flex align-items-center mb-2">
                                {renderCurrencyIcon(transaction.toCurrency, 24)}
                                <span className="ms-2 transaction-amount text-body">
                                  {estimatedAmount} {transaction.toCurrency}
                                </span>
                              </div>
                              <div className="transaction-address">
                                <span className="address-label">Address:</span>
                                <code className="address-value">{walletAddress}</code>
                              </div>
                              {extraId && (
                                <div className="transaction-extra-id">
                                  <span className="extra-id-label">Extra ID:</span>
                                  <code className="extra-id-value">{extraId}</code>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                  
                  <div className="d-grid gap-3">
                    <Button 
                      variant="primary"
                      onClick={checkTransactionStatus}
                      disabled={isLoading}
                      className="refresh-status-button"
                    >
                      <FontAwesomeIcon icon={faClock} className="me-2" spin={isLoading} />
                      Refresh Status
                    </Button>
                    <Button 
                      variant="outline-secondary"
                      onClick={resetExchange}
                      disabled={isLoading}
                      className="new-exchange-button"
                    >
                      <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
                      Start New Exchange
                    </Button>
                  </div>
                </div>
              </CSSTransition>
            )}
          </TransitionGroup>
        </Card.Body>
      </Card>
    
    {/* Fixed Rate Not Available Modal */}
    <Modal 
      show={showFixedRateModal} 
      onHide={() => setShowFixedRateModal(false)}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
          Fixed Rate Unavailable
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>Fixed rate exchange is not available for the selected currency pair:</p>
        <div className="d-flex align-items-center justify-content-center my-3">
          <div className="d-flex align-items-center">
            {renderCurrencyIcon(fromCurrency, 32)}
            <span className="ms-2 fw-bold">{fromCurrency}</span>
          </div>
          <FontAwesomeIcon icon={faArrowRight} className="mx-3" />
          <div className="d-flex align-items-center">
            {renderCurrencyIcon(toCurrency, 32)}
            <span className="ms-2 fw-bold">{toCurrency}</span>
          </div>
        </div>
        <p>Your exchange will continue with the standard rate method.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => setShowFixedRateModal(false)}>
          Continue with Standard Exchange
        </Button>
      </Modal.Footer>
    </Modal>
  </Container>
);
};

export default Exchange;
